import { PAGE_PATH } from '@/constants';
import { fetchProdcutVariants, fetchProductDetails } from '@/services';
import {
  useAppStore,
  useAuthStore,
  useCommodityStore,
  usePlaceOrderStore,
  useUserStore,
} from '@/store';
import { DiscountType, MemberState, PutOnSaleType, type AnyObj } from '@/types';
import type { Commodity } from '@/types/global-business';
import { businessUtils, toast } from '@/utils';
import NP from 'number-precision';

export default {
  /**
   * 根据 订单line 拼接商品渲染信息
   * @param data - 订单line
   * @returns
   */
  getLines: (data: AnyObj[]): Commodity.CardType[] => {
    if (!data?.length) return [];
    return data.map((it: AnyObj) => {
      const productVariant = it.productVariant;
      return {
        id: it.id,
        imgUrl:
          productVariant.featuredAsset?.source || productVariant.product.featuredAsset?.source,
        skus: businessUtils.sku.getText(productVariant.options),
        name: productVariant.name,
        price: it.unitPrice || productVariant.price,
        count: it.totalQuantity,
        _data: it,
      };
    });
  },
  /**
   * 获取商品定时销售配置信息
   * @param data
   * @returns
   */
  getTimedSaleConfig: (data: { putOnSaleType: PutOnSaleType; putOnSaleTime: string } | null) => {
    let config = { is: false, type: PutOnSaleType.immediate, time: '' };

    if (!data) return config;

    config = {
      is:
        data.putOnSaleType === PutOnSaleType.scheduled &&
        new Date(data.putOnSaleTime).getTime() > Date.now(),
      type: data.putOnSaleType,
      time: data.putOnSaleTime,
    };

    return config;
  },
  /**
   * 定时开售倒计时结束更新对应商品
   * @param id
   * @param data
   * @returns
   */
  updateRegularSales: async (id: string, data: AnyObj, type: UnionKey.CommodityDataStructure) => {
    try {
      if (!id || !data) {
        return console.log(`<定时开售倒计时结束参数错误>`, { id, data });
      }

      const res = await fetchProductDetails(id);
      // 替换 purchasePopupItemCacheRef 的定时开售相关字段
      const putOnSaleTime = res.customFields.putOnSaleTime;
      const putOnSaleType = res.customFields.putOnSaleType;

      if (type === 'normal') {
        data.customFields = {
          ...data.customFields,
          putOnSaleTime,
          putOnSaleType,
          _refreshRegularSales: true,
        };
      } else if (type === 'lines') {
        data.item.productVariant.product.customFields = {
          ...data.item.productVariant.product.customFields,
          putOnSaleTime,
          putOnSaleType,
          _refreshRegularSales: true,
        };
      } else {
        console.log(`<定时开售倒计时结束数据结构类型错误>`, type);
      }

      return data;
    } catch (error) {
      console.log(error);
    }
  },
  /**
   * 获取会员卡折扣比例
   */
  getMemberCardRate() {
    const authStore = useAuthStore();
    const userStore = useUserStore();
    // 折扣比例
    let rate = 0;
    if (
      !authStore.isLogin ||
      ![MemberState.normal, MemberState.unactivated].includes(userStore.userMemberInfo?.state)
    ) {
      // 取最大优惠的会员卡
      // console.log(`<取最大优惠的会员卡>`, userStore.maxDiscountMembershipPlan?.rightsDiscount?.discountRate);
      rate = (userStore.maxDiscountMembershipPlan?.rightsDiscount?.discountRate ?? 0) / 100;
    } else {
      // 取自己的会员卡
      // console.log(`<取自己的会员卡>`, userStore.userMemberInfo?.membershipPlan.rightsDiscount.discountRate);
      rate = (userStore.userMemberInfo?.membershipPlan.rightsDiscount.discountRate ?? 0) / 100;
    }
    return rate;
  },
  /**
   * 计算商品规格区间价格
   * @returns
   */
  computeRegionPrices() {
    const commodityStore = useCommodityStore();
    const details = commodityStore.commodityDetails;
    const userStore = useUserStore();
    const authStore = useAuthStore();

    if (!details) {
      return {
        displayMember: false,
        region: [0, 0],
        ltRegion: [0, 0],
      };
    }

    // 创建variants的副本，避免直接修改响应式数据
    const variantsCopy = [...(details.variants ?? [])];
    
    // 会员卡折扣比例
    const mcRate = businessUtils.commodity.getMemberCardRate();
    let displayMember = false;

    // 使用map返回新数组，而不是修改原数组
    const processedVariants = variantsCopy.map((it: AnyObject) => {
      const variant = { ...it };
      // 原价
      variant._vipPrice = variant.price;

      const mt = details.memberPriceActivityAmount?.memberPriceProductVariant?.find(
        (m: AnyObject) => m.productVariantId === variant.id
      );

      if (
        authStore.isLogin &&
        userStore.userMemberInfo &&
        userStore.userMemberInfo?.state === MemberState.normal
      ) {
        if (mt) {
          displayMember = true;
          variant._vipPrice =
            mt.discountType === DiscountType.FixedAmount
              ? mt.memberPriceAmount
              : NP.times(NP.divide(mt.memberDiscount, 100), variant.price);
        } else if (commodityStore.isVipProduct) {
          displayMember = true;
          variant._vipPrice = NP.times(mcRate, variant.price);
        }
      }

      return variant;
    });

    const priceArray = processedVariants.map((it: AnyObject) => it._vipPrice);
    const min = Math.min(...priceArray);
    const max = Math.max(...priceArray);
    const region = [min, min === max ? 0 : max];

    // 处理划线价
    let ltRegion = [0, 0];
    
    if (displayMember) {
      const pArr = processedVariants.map((it: AnyObject) => it.price);
      const pMin = Math.min(...pArr);
      const pMax = Math.max(...pArr);
      ltRegion = [pMin, pMin === pMax ? 0 : pMax];
    } else if (details.customFields.markingPrice) {
      ltRegion = [details.customFields.markingPrice, 0];
    }

    return {
      displayMember,
      region,
      ltRegion,
    };
  },
  /** 价格校验 - 一毛钱 */
  async verifyTenCentsHandler(amount: number, needFilterProductVariantId = '') {
    const limit = 10;
    if (!amount) {
      console.log(`商品价格信息错误，请检查`, amount);
    }

    // 如果小于一毛钱
    if (amount < limit) {
      const pages = getCurrentPages();
      const curPage = pages[pages.length - 1];

      // 获取购物车数据
      const placeOrderStore = usePlaceOrderStore();
      const shoppingTrolley = await placeOrderStore.getShoppingTrolley(true);
      let flatMapShoppingTrolley =
        shoppingTrolley?.shoppingTrolleyCardGroup?.flatMap((it) => it.commodityList) ?? [];

      // 如果当前页面是购物车页，切换规格时需排除自身商品后进行校验
      if (`/${curPage.route}` === PAGE_PATH.cart.route) {
        flatMapShoppingTrolley = flatMapShoppingTrolley.filter(
          (it) => it.skuId !== needFilterProductVariantId
        );
      }

      const prices = flatMapShoppingTrolley.map((it) => it.displayAmount);

      // 查找是否含有一分钱的商品
      const result = prices?.some((it) => it < limit);
      // 有则提示
      if (result) {
        toast.message(`心愿先生新客福利终身限购1次`);
        return false;
      }
    }
    // 无则通过
    return true;
  },
  /**
   * 指定用户购买
   * @param data - productPurchasePermission
   * @param isIgnorePrompt - 是否忽略提示操作
   */
  specifyMemberPurchase(data: AnyObject) {
    if (!data?.isMembershipPlanPurchase) return false;

    const userStore = useUserStore();

    // 判断用户的会员卡是否在可购买的会员卡列表里
    const filterResult =
      data.membershipPlans.filter(
        (it: AnyObject) => it.id === userStore.userMemberInfo?.membershipPlan?.id
      ).length > 0;

    // 用户不是会员
    if (!userStore.isMember) {
      return true;
    }

    // 可购买，则走正常购买流程
    if (filterResult) {
      return false;
    }

    // 用户是会员，但不可购买
    if (userStore.isMember && !filterResult) {
      return true;
    }
  },
  /** 组合商品的规格数据 */
  async combineVariants(data: AnyObject, options: { isLoading: boolean } = { isLoading: true }) {
    let cancelLoading = null;
    try {
      if (data) {
        if (options.isLoading) cancelLoading = toast.loading();
        const res = await fetchProdcutVariants(data.id);
        data.variants = res.variants;
        data.optionGroups = res.optionGroups;
      } else {
        console.log(`商品数据有误，请检查`);
      }
      cancelLoading?.();
      return data;
    } catch (error) {
      cancelLoading?.();
      console.log(error);
    }
  },
  getState(data: AnyObject) {
    return {
      enabledFlag: data?.enabled,
      allSkuSoltOutFlag: !data.productTotalStock,
    };
  },
};
